import React, { useCallback, useEffect, useState } from "react";
import { useEscrowClient } from "../hooks/useEscrowClient";

interface ArbitratorInfo {
  address: string;
  registered: boolean;
  active: boolean;
  feeRateBps: number;
  casesHandled: number;
  casesWon: number;
  casesLost: number;
  avgResolution: number;
}

const STORAGE_KEY = "motosettle_known_arbitrators";

function loadKnownAddresses(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveKnownAddresses(addresses: string[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(addresses));
}

function truncateAddr(addr: string): string {
  if (addr.length <= 20) return addr;
  return `${addr.slice(0, 14)}...${addr.slice(-8)}`;
}

export function ArbitratorMarketplacePage() {
  const client = useEscrowClient();

  // My profile
  const [myInfo, setMyInfo] = useState<ArbitratorInfo | null>(null);
  const [myLoading, setMyLoading] = useState(false);

  // Registration
  const [regFee, setRegFee] = useState(200);
  const [regLoading, setRegLoading] = useState(false);
  const [regStatus, setRegStatus] = useState("");

  // Search
  const [searchAddr, setSearchAddr] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");

  // Known arbitrators list
  const [arbitrators, setArbitrators] = useState<ArbitratorInfo[]>([]);
  const [listLoading, setListLoading] = useState(false);

  const fetchArbitratorInfo = useCallback(
    async (addr: string): Promise<ArbitratorInfo | null> => {
      if (!client) return null;
      try {
        const resolved = await client.provider.getPublicKeyInfo(addr, true);
        if (!resolved) return null;

        const result = await client.arbRegistryContract.getInfo(resolved);
        if ("error" in result) return null;

        const props = result.properties;
        return {
          address: addr,
          registered: Boolean(props.registered),
          active: Boolean(props.active),
          feeRateBps: Number(props.feeRateBps),
          casesHandled: Number(props.casesHandled),
          casesWon: Number(props.casesWon),
          casesLost: Number(props.casesLost),
          avgResolution: Number(props.avgResolution),
        };
      } catch (e: any) {
        console.error(`[ArbitratorMarketplace] Error fetching ${addr}:`, e);
        return null;
      }
    },
    [client],
  );

  // Fetch connected wallet's arbitrator status
  useEffect(() => {
    if (!client?.walletAddress) {
      setMyInfo(null);
      return;
    }
    setMyLoading(true);
    fetchArbitratorInfo(client.walletAddress).then((info) => {
      setMyInfo(info);
      setMyLoading(false);
    });
  }, [client?.walletAddress, fetchArbitratorInfo]);

  // Load known arbitrators
  useEffect(() => {
    if (!client) return;
    const known = loadKnownAddresses();
    if (known.length === 0) return;

    setListLoading(true);
    Promise.all(known.map(fetchArbitratorInfo)).then((results) => {
      const valid = results.filter(
        (r): r is ArbitratorInfo => r !== null && r.registered,
      );
      setArbitrators(valid);
      saveKnownAddresses(valid.map((a) => a.address));
      setListLoading(false);
    });
  }, [client, fetchArbitratorInfo]);

  // Register as arbitrator
  const handleRegister = async () => {
    if (!client) return;
    setRegLoading(true);
    setRegStatus("");
    try {
      const result = await client.arbRegistryContract.register(BigInt(regFee));
      if ("error" in result) {
        setRegStatus(`Error: ${result.error}`);
        setRegLoading(false);
        return;
      }
      if (result.revert) {
        setRegStatus(`Revert: ${result.revert}`);
        setRegLoading(false);
        return;
      }
      setRegStatus("Simulacion OK. Enviando transaccion...");
      const receipt = await result.sendTransaction({
        signer: null,
        mldsaSigner: null,
        refundTo: client.walletAddress,
        maximumAllowedSatToSpend: 100_000n,
        feeRate: 10,
        network: client.network,
      });
      setRegStatus(`Registrado! Tx: ${receipt.transactionId}`);
      // Refresh my info
      const info = await fetchArbitratorInfo(client.walletAddress);
      setMyInfo(info);
    } catch (e: any) {
      setRegStatus(`Error: ${e.message}`);
    }
    setRegLoading(false);
  };

  // Search
  const handleSearch = async () => {
    const addr = searchAddr.trim();
    if (!addr || !client) return;

    setSearchError("");
    setSearchLoading(true);

    const info = await fetchArbitratorInfo(addr);
    setSearchLoading(false);

    if (!info) {
      setSearchError("No se pudo resolver la direccion.");
      return;
    }
    if (!info.registered) {
      setSearchError("Esta direccion no esta registrada como arbitrador.");
      return;
    }

    setArbitrators((prev) => {
      const exists = prev.some((a) => a.address === addr);
      const next = exists
        ? prev.map((a) => (a.address === addr ? info : a))
        : [...prev, info];
      saveKnownAddresses(next.map((a) => a.address));
      return next;
    });
    setSearchAddr("");
  };

  const removeArbitrator = (addr: string) => {
    setArbitrators((prev) => {
      const next = prev.filter((a) => a.address !== addr);
      saveKnownAddresses(next.map((a) => a.address));
      return next;
    });
  };

  const winRate = (arb: ArbitratorInfo) => {
    if (arb.casesHandled === 0) return "N/A";
    return `${Math.round((arb.casesWon / arb.casesHandled) * 100)}%`;
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1>Arbitrator Marketplace</h1>
        <p>Registrate como arbitrador o busca arbitradores disponibles para tus escrows.</p>
      </div>

      {!client && (
        <div className="alert alert-info" style={{ maxWidth: 700 }}>
          Conecta tu wallet para ver tu estado y buscar arbitradores.
        </div>
      )}

      {/* ─── My Arbitrator Profile ──────────────────────────── */}
      {client && (
        <div className="card" style={{ maxWidth: 700, marginBottom: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h3 style={{ margin: 0 }}>Mi Perfil de Arbitrador</h3>
            {myLoading && <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>Cargando...</span>}
          </div>

          {!myLoading && myInfo?.registered ? (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                <span
                  style={{
                    padding: "3px 10px",
                    borderRadius: 4,
                    fontSize: 12,
                    fontWeight: 600,
                    background: myInfo.active ? "rgba(34,197,94,0.15)" : "rgba(239,68,68,0.15)",
                    color: myInfo.active ? "#22c55e" : "#ef4444",
                  }}
                >
                  {myInfo.active ? "ACTIVO" : "INACTIVO"}
                </span>
                <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>
                  Fee: {(myInfo.feeRateBps / 100).toFixed(1)}%
                </span>
              </div>
              <p style={{ fontFamily: "monospace", fontSize: 12, color: "var(--text-secondary)", margin: "0 0 16px", wordBreak: "break-all" }}>
                {client.walletAddress}
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, textAlign: "center" }}>
                <StatBox label="Casos" value={myInfo.casesHandled} />
                <StatBox label="Pro-Buyer" value={myInfo.casesWon} color="#22c55e" />
                <StatBox label="Pro-Seller" value={myInfo.casesLost} color="#60a5fa" />
                <StatBox label="Balance" value={winRate(myInfo)} />
              </div>
            </>
          ) : !myLoading ? (
            /* Registration form */
            <div>
              <p style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 16 }}>
                No estas registrado como arbitrador. Registrate para resolver disputas y ganar fees.
              </p>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 13, marginBottom: 6 }}>
                  Fee Rate: <strong>{(regFee / 100).toFixed(1)}%</strong> ({regFee} bps)
                </label>
                <input
                  type="range"
                  min={10}
                  max={500}
                  step={10}
                  value={regFee}
                  onChange={(e) => setRegFee(Number(e.target.value))}
                  style={{ width: "100%" }}
                />
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--text-secondary)" }}>
                  <span>0.1%</span>
                  <span>5.0% (max)</span>
                </div>
              </div>
              <button
                className="btn btn-primary"
                disabled={regLoading}
                onClick={handleRegister}
              >
                {regLoading ? "Registrando..." : "Registrarme como Arbitrador"}
              </button>
              {regStatus && (
                <p style={{
                  marginTop: 12,
                  fontSize: 13,
                  color: regStatus.startsWith("Registrado") ? "#22c55e" : regStatus.startsWith("Simulacion") ? "var(--teal)" : "#ef4444",
                }}>
                  {regStatus}
                </p>
              )}
            </div>
          ) : null}
        </div>
      )}

      {/* ─── Search ──────────────────────────────────────────── */}
      {client && (
        <div className="card" style={{ maxWidth: 700, marginBottom: 24 }}>
          <h3 style={{ marginTop: 0 }}>Buscar Arbitrador</h3>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              type="text"
              placeholder="Direccion OP_NET (opt1...)"
              value={searchAddr}
              onChange={(e) => setSearchAddr(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              style={{
                flex: 1,
                padding: "8px 12px",
                borderRadius: 6,
                border: "1px solid rgba(255,255,255,0.15)",
                background: "rgba(255,255,255,0.05)",
                color: "inherit",
                fontSize: 14,
              }}
            />
            <button
              className="btn btn-primary"
              disabled={searchLoading || !searchAddr.trim()}
              onClick={handleSearch}
            >
              {searchLoading ? "..." : "Buscar"}
            </button>
          </div>
          {searchError && (
            <p style={{ color: "#f87171", marginTop: 8, fontSize: 13 }}>{searchError}</p>
          )}
        </div>
      )}

      {/* ─── Arbitrator List ──────────────────────────────────── */}
      {client && (
        <div style={{ maxWidth: 700 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <h3 style={{ margin: 0 }}>Arbitradores Conocidos</h3>
            {listLoading && <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>Cargando...</span>}
          </div>
          <p style={{ fontSize: 11, color: "var(--text-muted)", margin: "0 0 16px" }}>
            <strong>Pro-Buyer:</strong> disputas resueltas a favor del comprador.{" "}
            <strong>Pro-Seller:</strong> trades liberados sin disputa + disputas a favor del vendedor.
          </p>

          {arbitrators.length === 0 && !listLoading ? (
            <div className="card">
              <p style={{ color: "var(--text-secondary)", margin: 0, textAlign: "center" }}>
                Aun no has buscado arbitradores. Usa el buscador para agregar direcciones.
              </p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {arbitrators.map((arb) => (
                <div
                  key={arb.address}
                  className="card"
                  style={{
                    borderLeft: `3px solid ${arb.active ? "#22c55e" : "#ef4444"}`,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                        <span
                          style={{
                            padding: "2px 8px",
                            borderRadius: 4,
                            fontSize: 10,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            background: arb.active ? "rgba(34,197,94,0.15)" : "rgba(239,68,68,0.15)",
                            color: arb.active ? "#22c55e" : "#ef4444",
                          }}
                        >
                          {arb.active ? "Activo" : "Inactivo"}
                        </span>
                        <span style={{ fontSize: 13, fontWeight: 600 }}>
                          Fee: {(arb.feeRateBps / 100).toFixed(1)}%
                        </span>
                      </div>
                      <p style={{
                        fontFamily: "monospace",
                        fontSize: 12,
                        color: "var(--text-secondary)",
                        margin: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}>
                        {arb.address}
                      </p>
                    </div>
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{ fontSize: 11, padding: "4px 8px", marginLeft: 8 }}
                      onClick={() => removeArbitrator(arb.address)}
                      title="Quitar de la lista"
                    >
                      x
                    </button>
                  </div>

                  <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, 1fr)",
                    gap: 8,
                    textAlign: "center",
                    marginTop: 12,
                    padding: "12px 0 0",
                    borderTop: "1px solid rgba(255,255,255,0.06)",
                  }}>
                    <StatBox label="Casos" value={arb.casesHandled} small />
                    <StatBox label="Pro-Buyer" value={arb.casesWon} color="#22c55e" small />
                    <StatBox label="Pro-Seller" value={arb.casesLost} color="#60a5fa" small />
                    <StatBox label="Balance" value={winRate(arb)} small />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatBox({ label, value, color, small }: {
  label: string;
  value: number | string;
  color?: string;
  small?: boolean;
}) {
  return (
    <div>
      <div style={{ fontSize: small ? 16 : 22, fontWeight: 700, color: color ?? "inherit" }}>
        {value}
      </div>
      <div style={{ fontSize: small ? 10 : 11, color: "var(--text-secondary)" }}>{label}</div>
    </div>
  );
}
